if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.productData = JSON.parse(this.querySelector('.product-json').innerHTML);
      this.productId = String(this.dataset.productId || '');
      this.inputs = this.querySelectorAll('.option-input');
      this._variantChangeTimer = null;
      this._variantChangeSeq = 0;

      this.inputs.forEach((input) => {
        input.addEventListener('change', this.onVariantChange.bind(this));
      });

      this.updateSoldOutStates();
      this.updateSelectedValueLabels();
    }

    getSelectedOptions() {
      return Array.from(this.querySelectorAll('.option-input:checked')).map(el => el.value);
    }

    findVariantByOptions(optionsArray) {
      return this.productData?.variants?.find(v =>
        v.options.every((opt, i) => opt === optionsArray[i])
      );
    }

    updateSoldOutStates() {
      const selectedOptions = this.getSelectedOptions();

      this.inputs.forEach((input) => {
        const label = input.closest('label');
        if (!label) return;

        const optionIndex = this.getOptionIndexForInput(input);
        const candidate = [...selectedOptions];

        while (candidate.length < this.productData?.options?.length) candidate.push(null);

        candidate[optionIndex] = input.value;
        const isAvailable = this.isCandidateAvailable(candidate);

        label.classList.toggle('is-sold-out', !isAvailable);
        input.classList.toggle('is-sold-out', !isAvailable);
      });
    }

    getOptionIndexForInput(input) {
      const fieldset = input.closest('fieldset');
      const allFieldsets = Array.from(this.querySelectorAll('fieldset.options__block'));
      return Math.max(0, allFieldsets.indexOf(fieldset));
    }

    isCandidateAvailable(candidateOptions) {
      const variants = this.productData?.variants || [];
      const matches = variants.filter(v => {
        return v.options.every((opt, idx) => {
          const wanted = candidateOptions[idx];
          return wanted == null ? true : opt === wanted;
        });
      });

      if (!matches.length) return false;

      return matches.some(v => v.available);
    }

    onVariantChange(e) {
      this.querySelectorAll('.options-block').forEach(el => el.classList.remove('hidden'));

      // Debounce to avoid firing 5x when user clicks quickly
      clearTimeout(this._variantChangeTimer);
      const seq = ++this._variantChangeSeq;

      this._variantChangeTimer = setTimeout(() => {
        if (seq !== this._variantChangeSeq) return;
        this._applyVariantChange();
      }, 80);
    }

    _applyVariantChange() {
      const selectedOptions = this.getSelectedOptions();
      const matchingVariant = this.findVariantByOptions(selectedOptions);

      this.updateSoldOutStates();
      this.updateSelectedValueLabels();

      if (matchingVariant) {
        document.dispatchEvent(new CustomEvent('variant:changed', {
          detail: { variant: matchingVariant, productId: this.productId }
        }));


        const wrapper = this.closest('[data-picker-form-wrapper]');
        if (!wrapper) return;

        const productForm = wrapper.querySelector('product-form');
        const formEl = productForm?.querySelector('form');
        const idInput = formEl?.querySelector('input[name="id"]');

        if (idInput) {
          idInput.value = String(matchingVariant.id);

          idInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.notifyVariantInputChanged(idInput);
        }

        formEl?.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: { variant: matchingVariant }
          })
        );
        
        const atcPriceItem = document.querySelector('.bb-price-item');
        const productPrice = document.querySelector('.product-info .product-price .product-price-item');
        const productComparePrice = document.querySelector('.product-info .product-price .product-compare-price-item');

        if (atcPriceItem) atcPriceItem.textContent = matchingVariant.price;
        const formatted = window.Shopify?.formatMoney
          ? window.Shopify.formatMoney(matchingVariant.shopify_price, window.theme?.moneyFormat || window.Shopify.money_format)
          : (matchingVariant.shopify_price / 100).toFixed(2);

        if (productPrice) productPrice.textContent = formatted != "NaN" ? formatted : matchingVariant.price;

        if (productComparePrice) {
          if (matchingVariant.compare_at_price) {
            productComparePrice.classList.remove('hidden');
            productComparePrice.textContent = matchingVariant.compare_at_price;
          } else {
            productComparePrice.classList.add('hidden');
          }
        }

        // Updating url
        const newUrl = `/products/${matchingVariant.handle}?variant=${matchingVariant.id}`;
        window.history.replaceState({}, '', newUrl);

        this.updateGallery(matchingVariant);
      }
    }

    notifyVariantInputChanged(idInput) {
      if (!idInput) return;

      idInput.dispatchEvent(new Event('change', { bubbles: true }));

      requestAnimationFrame(() => {
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      setTimeout(() => {
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }, 60);
    }


    async updateGallery(variant) {
      const wrapper = document.querySelector('[data-product-media-wrapper]');
      if (!wrapper) return;

      const mediaJsonEl = wrapper.querySelector('[data-variant-media-json]');
      if (!mediaJsonEl) return;

      const mediaMap = JSON.parse(mediaJsonEl.textContent || "{}");
      const media = mediaMap[String(variant.id)];
      if (!media) return;

      await this.preloadVariantMedia(media);

      this.applyVariantMedia(wrapper, media);

      const cs = wrapper.querySelector('custom-swiper');
      if (cs?.mainSwiper) {
        cs.mainSwiper.update();
        cs.mainSwiper.slideTo(0, 0);
      }
      if (cs?.thumbsSwiper) {
        cs.thumbsSwiper.update();
        cs.thumbsSwiper.slideTo(0, 0);
      }
    }

    preloadVariantMedia(media) {
      const tasks = [];

      // preload images
      (media.images || []).forEach(i => {
        if (!i?.src) return;
        tasks.push(new Promise((resolve) => {
          const img = new Image();
          img.decoding = "async";
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = i.src;
          // If supported, decode avoids flash in some browsers
          if (img.decode) img.decode().then(resolve).catch(resolve);
        }));
      });

      // "preload" video (best-effort)
      if (media.video) {
        tasks.push(new Promise((resolve) => {
          const v = document.createElement('video');
          v.preload = 'auto';
          v.muted = true;
          v.playsInline = true;
          v.oncanplay = () => resolve();
          v.onerror = () => resolve();
          v.src = media.video;
          v.load();
          setTimeout(resolve, 1200); // don’t block forever
        }));
      }

      return Promise.all(tasks);
    }

    applyVariantMedia(wrapper, media) {
      // Optional: fade without layout jump (CSS below)
      wrapper.classList.add('is-updating');

      const mainSlides = wrapper.querySelectorAll('[data-main-swiper] .product-main-slide');
      const thumbSlides = wrapper.querySelectorAll('[data-thumbnails-swiper] .product-thumbs-slide');

      // Video: if you always render a video slide first when present, keep it and just swap src.
      const mainVideo = wrapper.querySelector('[data-main-swiper] video');
      const thumbVideo = wrapper.querySelector('[data-thumbnails-swiper] video');

      if (media.video && mainVideo) {
        if (mainVideo.src !== media.video) {
          mainVideo.src = media.video;
          mainVideo.load();
          mainVideo.play?.().catch(() => {});
        }
      }
      if (media.video && thumbVideo) {
        if (thumbVideo.src !== media.video) {
          thumbVideo.src = media.video;
          thumbVideo.load();
          thumbVideo.play?.().catch(() => {});
        }
      }

      // Images: set src for existing img tags (you already have fixed slide count via limit)
      const mainImgs = wrapper.querySelectorAll('[data-main-swiper] img.product-img');
      const thumbImgs = wrapper.querySelectorAll('[data-thumbnails-swiper] img.thumb-img');

      (media.images || []).forEach((item, idx) => {
        if (mainImgs[idx]) {
          mainImgs[idx].src = item.src;
          // If your image-snippet outputs srcset/sizes, also update those consistently
          mainImgs[idx].srcset = "";
        }
        if (thumbImgs[idx]) {
          thumbImgs[idx].src = item.thumb || item.src;
          thumbImgs[idx].srcset = "";
        }
      });

      // If fewer images than existing, you can hide the extra slides
      for (let i = (media.images || []).length; i < mainImgs.length; i++) {
        const slide = mainImgs[i].closest('.product-main-slide');
        if (slide) slide.classList.add('hidden');
      }
      for (let i = 0; i < (media.images || []).length; i++) {
        const slide = mainImgs[i]?.closest('.product-main-slide');
        if (slide) slide.classList.remove('hidden');
      }

      // Remove fade class next frame
      requestAnimationFrame(() => wrapper.classList.remove('is-updating'));
    }

    updateSelectedValueLabels() {
      const blocks = this.querySelectorAll('.options__block-wrapper');

      blocks.forEach((block, idx) => {
        const selected = block.querySelector('.option-input:checked')?.value || '';
        const out = block.querySelector('[data-selected-value]');
        const outText = block.querySelector('[data-selected-value-text]');

        if (outText) outText.textContent = selected;

        if (out && !outText) out.textContent = selected;
      });
    }


  }

  customElements.define('variant-picker', VariantPicker)
}
